/**
 * Input inline Add New (epic #581) — what the Input step's inline "Add New"
 * form may offer, given the module the practice item sits in.
 *
 * The Problem Solving course teaches kinds of value a module at a time. The
 * inline form offers only the kinds the item's module (and the ones before it)
 * has taught, so a Data In and Out learner is not shown "Record". Variables
 * that already exist are NOT gated — only what the inline form offers.
 *
 * Keyed by module SLUG, not order: ps-m12 does not exist, and ps-m13/ps-m14
 * are the capstone and what comes next. Anything else — the C Programming
 * course, no course, a missing field (the production backend before this
 * ships), a guest, an unknown module — offers everything: today's behaviour.
 *
 * The builder page sets `itemPlacement` in the config store from the item's
 * /progress response (`course_slug`, `module_slug`); `?dev=true&module=ps-m2`
 * sets it by hand for a preview.
 */
import useConfigStore from '@/stores/useConfigStore';

export const PS_COURSE_SLUG = 'problem-solving-flow-first-code-next-a-guided-experience';

// Every kind the inline form knows, in the order it shows them.
export const ALL_INPUT_KINDS = ['whole', 'decimal', 'character', 'text', 'yesno', 'list', 'record'];

// What each kind is called (always words) and the C type it declares.
export const INPUT_KIND_INFO = {
  whole:     { label: 'Whole number', cType: 'int' },
  decimal:   { label: 'Decimal',      cType: 'float' },
  character: { label: 'Character',    cType: 'char' },
  text:      { label: 'Text',         cType: 'char[]' },
  yesno:     { label: 'Yes/no',       cType: 'bool' },
  list:      { label: 'List',         cType: 'array' },
  record:    { label: 'Record',       cType: 'struct' },
};

// Module number from which each kind is offered in the Problem Solving course.
const PS_KIND_FROM_MODULE = {
  whole: 1, decimal: 1, character: 1,
  text: 2,
  yesno: 4,
  list: 6,
  record: 9,
};

// From this module on, the inline form offers everything (and the full form).
const PS_EVERYTHING_FROM = 10;

/** The Problem Solving module number in a slug like `ps-m6`, or null. */
export function psModuleNumber(moduleSlug) {
  const m = String(moduleSlug || '').trim().match(/^ps-m(\d+)$/);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Is the inline form limited for this placement? Only a Problem Solving item
 * in a known module before ps-m10 is.
 */
function gatedModuleNumber({ courseSlug, moduleSlug } = {}) {
  if (courseSlug !== PS_COURSE_SLUG) return null;
  const n = psModuleNumber(moduleSlug);
  if (n == null || n < 1 || n >= PS_EVERYTHING_FROM) return null;
  return n;
}

/** The kinds the inline form offers for an item's course + module. */
export function inputKindsFor(placement) {
  const n = gatedModuleNumber(placement || {});
  if (n == null) return [...ALL_INPUT_KINDS];
  return ALL_INPUT_KINDS.filter((k) => PS_KIND_FROM_MODULE[k] <= n);
}

/**
 * True when nothing is held back — the form then also links to the full
 * "New Variable" form for what it does not cover (constants, globals…).
 */
export function offersEverything(placement) {
  return gatedModuleNumber(placement || {}) == null;
}

/**
 * The /progress response's placement. A missing or malformed field is null,
 * which reads as "offer everything".
 */
export function placementFromProgress(data) {
  if (!data || typeof data !== 'object') return null;
  const courseSlug = typeof data.course_slug === 'string' ? data.course_slug : null;
  const moduleSlug = typeof data.module_slug === 'string' ? data.module_slug : null;
  if (!courseSlug && !moduleSlug) return null;
  return { courseSlug, moduleSlug };
}

/**
 * A safe way to SEE a module's limits before the backend ships:
 * `?dev=true&module=ps-m2`. Both are needed, so a stray `module` on a
 * learner's link does nothing. Returns the module slug, or null.
 */
export function moduleFromDevQuery(searchParams) {
  if (!searchParams || typeof searchParams.get !== 'function') return null;
  if (searchParams.get('dev') !== 'true') return null;
  const mod = (searchParams.get('module') || '').trim();
  return mod || null;
}

/** The placement a dev module override stands for (the Problem Solving course). */
export function devPlacement(moduleSlug) {
  return moduleSlug ? { courseSlug: PS_COURSE_SLUG, moduleSlug } : null;
}

/**
 * Has the item's module taught pointers (addresses, `&x` / `*p`)? Only a
 * Problem Solving item before ps-m10 says no; anything else says yes.
 */
export function pointersTaught(placement) {
  return gatedModuleNumber(placement || {}) == null;
}

/** Hook: pointersTaught for the item being built. */
export function usePointersTaught() {
  const placement = useConfigStore((s) => s.itemPlacement);
  return pointersTaught(placement);
}

/** Hook: the kinds the inline form offers for the item being built. */
export function useInputKinds() {
  const placement = useConfigStore((s) => s.itemPlacement);
  return {
    kinds: inputKindsFor(placement),
    everything: offersEverything(placement),
  };
}
